import { browserLogger } from "@@/settings";
import type { SocketEvent } from "@towni/common";
import { emptyArrayOf, exists, generateComboId } from "@towni/common";
import * as React from "react";
import { useCallback } from "react";
import { WebSocketContext } from "./socket-context";

type SocketListener<AnEvent extends SocketEvent = SocketEvent> = {
    readonly eventType: AnEvent["type"];
    readonly rooms?: string[];
    readonly onEvent: (
        eventData: AnEvent,
        rooms: string[] | undefined,
    ) => void | Promise<void>;
};

/** Creates a single socket event listener configuration */
const createSocketEventListener = <AnEvent extends SocketEvent>(params: {
    readonly eventType: AnEvent["type"];
    readonly rooms?: string[];
    readonly onEvent: (
        eventData: AnEvent,
        rooms: string[] | undefined,
    ) => void | Promise<void>;
}): SocketListener<AnEvent> => {
    return {
        eventType: params.eventType,
        onEvent: params.onEvent,
        rooms: params.rooms,
    } as SocketListener<AnEvent>;
};

/** Creates multiple socket event listeners for different event types and rooms */
const createSocketEventListeners = <AnEvent extends SocketEvent>(params: {
    readonly eventTypes: AnEvent["type"][];
    readonly rooms?: string[];
    readonly onEvent: (
        eventData: AnEvent,
        rooms: string[] | undefined,
    ) => void | Promise<void>;
}): SocketListener<AnEvent>[] => {
    return params.eventTypes.flatMap(type => {
        if (params.rooms?.length) {
            // Create a listener configuration for each room
            return createSocketEventListener<AnEvent>({
                eventType: type,
                rooms: params.rooms,
                onEvent: params.onEvent,
            });
        }
        // Create a single listener configuration without a room
        return createSocketEventListener<AnEvent>({
            eventType: type,
            onEvent: params.onEvent,
        });
    });
};

type UseSocketOptions = {
    /** Enable verbose logging for socket events */
    readonly verbose?: boolean;
};

type SocketEventHandler = (
    event: SocketEvent,
    rooms: string[] | undefined,
) => void | Promise<void>;

/**
 * Custom hook for managing web socket connections and event listeners.
 * It handles socket registration, cleanup and reconnection logic.
 */
const useSocket = <AnEvent extends SocketEvent>(
    listenFor: SocketListener<AnEvent>[] = emptyArrayOf<
        SocketListener<AnEvent>
    >(),
    options: UseSocketOptions = {},
) => {
    const {
        socket,
        connected: socketConnected,
        localConnectionId: localSocketConnectionId,
    } = React.useContext(WebSocketContext);

    // Memoize the registration ID to avoid unnecessary re-registrations
    // Generate a new ID only when the component first mounts or socket reconnects
    const registrationId = React.useMemo(() => {
        const newId = generateComboId();
        browserLogger.info("🔌 ws generate registrationId:", {
            newId,
        });
        return newId;
    }, []); // Empty dependency array - stable for component lifetime

    // Memoize the listener configuration
    // to avoid re-creating it on every render
    // This ensures that the listener configuration is stable
    // and only changes when the registrationId or listenFor changes
    // This is important for the socket event handler to work correctly
    // and not re-register listeners unnecessarily
    const listenerConfig = React.useMemo(() => {
        return {
            regId: registrationId,
            rooms: Array.from(
                new Set<string>(
                    listenFor
                        .map(item => item.rooms)
                        .flat()
                        .filter(exists),
                ),
            ),
        };
    }, [registrationId, listenFor]);

    // Helper function to check if room matches the listener's room
    const isRoomMatch = useCallback(
        (
            eventRooms: string[] | undefined,
            listenerRooms: string[] | undefined,
        ) => {
            // Both are undefined/null, so they match
            if (!eventRooms && !listenerRooms) return true;
            // One is undefined/null, the other is not, so they don't match
            if (!eventRooms || !listenerRooms) return false;
            // Array of rooms includes the listener's room
            return eventRooms.some(room => listenerRooms.includes(room));
        },
        [],
    );

    React.useEffect(() => {
        if (
            !socket ||
            !socketConnected ||
            !localSocketConnectionId ||
            !listenFor.length
        ) {
            browserLogger.log("🔌 ws no socket or listenFor", {
                socket,
                socketConnected,
                localSocketConnectionId,
                listenFor,
                registrationId,
            });
            return;
        }

        const activeListeners = new Map<string, SocketEventHandler>();
        // Track processed events to prevent duplicate processing within this registration
        const MAX_EVENTS = 1000;
        const processedEvents = new Set<string>();
        const eventQueue: string[] = [];

        const addProcessedEvent = (eventKey: string) => {
            if (!processedEvents.has(eventKey)) {
                processedEvents.add(eventKey);
                eventQueue.push(eventKey);

                // Remove oldest when we exceed limit
                if (eventQueue.length > MAX_EVENTS) {
                    const oldest = eventQueue.shift()!;
                    processedEvents.delete(oldest);
                }
            }
        };

        const createEventHandler = (
            listener: SocketListener<AnEvent>,
        ): SocketEventHandler => {
            return (event: SocketEvent, rooms: string[] | undefined) => {
                try {
                    const roomMatch = isRoomMatch(rooms, listener.rooms);
                    const eventTypeMatch = event.type === listener.eventType;

                    if (roomMatch && eventTypeMatch) {
                        // Create a unique key for this event to prevent duplicate processing
                        const eventKey = `${event._id}-${event.type}`;

                        if (processedEvents.has(eventKey)) {
                            browserLogger.info("🔌 ws event deduplicated", {
                                eventId: event._id,
                                eventKey,
                                eventType: event.type,
                                rooms,
                                listenerRoom: listener.rooms,
                                registrationId,
                                event,
                            });
                            return;
                        }

                        // Mark this event as processed
                        addProcessedEvent(eventKey);

                        // Safe to cast since we've verified the type matches
                        // TODO: Not sure if second parameter should preferably
                        // TODO: be the `event.for` or `listener.rooms`. Don't know what's best.
                        void listener.onEvent(event as AnEvent, listener.rooms);
                        browserLogger.info("🥳 socket event received", {
                            eventId: event._id,
                            eventKey,
                            eventType: event.type,
                            rooms,
                            listenerRoom: listener.rooms,
                            registrationId,
                            event,
                        });
                    }
                } catch (error) {
                    browserLogger.error("🔌 socket event handler error", {
                        error,
                        eventType: listener.eventType,
                        rooms,
                        registrationId,
                        listenerRooms: listener.rooms,
                    });
                }
            };
        };

        const onConnect = () => {
            browserLogger.log("🔌 ws (re)connect", { registrationId });
            // Clean up existing listeners before re-registering
            activeListeners.forEach((handler, eventType) => {
                socket.off(eventType, handler);
            });
            activeListeners.clear();
            // Clear processed events on reconnect to ensure fresh state
            processedEvents.clear();
            // Re-register the listeners
            socket.emit("register", listenerConfig);
        };

        const onRegistered = (data: { regId: string; rooms: string[] }) => {
            if (registrationId !== data.regId) {
                browserLogger.warn("🔌 ws registered ID mismatch", {
                    registrationId,
                    data,
                });
                return;
            }
            browserLogger.log("🔌 ws registered successfully", {
                registrationId,
                rooms: data.rooms,
            });

            // Register event listeners
            listenFor.forEach(listener => {
                const handler = createEventHandler(listener);
                const key = `${listener.eventType}-${listener.rooms || "default"}`;

                // Avoid duplicate registration of listeners
                if (!activeListeners.has(key)) {
                    activeListeners.set(key, handler);
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    socket.on(listener.eventType, handler as any);
                    browserLogger.log("🔌 ws registered listener", {
                        eventType: listener.eventType,
                        rooms: listener.rooms,
                        registrationId,
                    });
                }
            });
        };

        // Set up verbose logging if enabled
        const verboseHandler = options.verbose
            ? (...args: unknown[]) => {
                  browserLogger.log("[verbose] 🔌 ws " + socket.id, ...args);
              }
            : undefined;

        // Register core event handlers
        socket.on("connect", onConnect);
        socket.on("reconnect", onConnect);
        socket.on("registered", onRegistered);

        if (verboseHandler) {
            socket.onAny(verboseHandler);
        }

        // Initial registration
        browserLogger.log("🔌 ws emit register", {
            connected: socket.connected,
            registrationId,
        });
        socket.emit("register", listenerConfig);

        // Cleanup
        return () => {
            browserLogger.log("🔌 ws unregister to events", { registrationId });
            socket.off("connect", onConnect);
            socket.off("reconnect", onConnect);
            socket.off("registered", onRegistered);
            if (verboseHandler) {
                socket.offAny(verboseHandler);
            }
            activeListeners.forEach((handler, key) => {
                // Extract the event type from the key
                // The key is in the format "eventType-room" or "eventType-default"
                // so we split it to get the event type
                const [eventType] = key.split("-");
                socket.off(eventType, handler);
            });
            activeListeners.clear();
        };
    }, [
        isRoomMatch,
        listenerConfig,
        listenFor,
        localSocketConnectionId,
        options.verbose,
        registrationId,
        socket,
        socketConnected,
    ]);

    return [socketConnected];
};
export { createSocketEventListener, createSocketEventListeners, useSocket };
export type { SocketListener, UseSocketOptions };
